import React, { useEffect } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formFieldStyles } from './FormField.styles';

const SelectField = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  formData = {},
}) => {
  const { t } = useTranslation();

  let finalField = field;
    if (field.name === 'teamId' && field.allTeams && formData) {
    const selectedGroupId = formData.groupId;
    const filteredTeams = selectedGroupId 
      ? field.allTeams.filter(t => t.groupId === selectedGroupId)
      : [];
    
    finalField = {
      ...field,
      options: filteredTeams.map(t => ({ value: t.id, label: t.name })),
      disabled: disabled || !selectedGroupId,
    };
  }
  
  if (field.name === 'contractId' && typeof field.options === 'function') {
    const dynamicOptions = field.options(formData);
    finalField = {
      ...field,
      options: dynamicOptions,
      disabled: disabled || dynamicOptions.length === 0,
    };
  }

  const handleChange = (event, newValue) => {
    let changedValue;

    if (finalField.type === 'autocomplete') {
      changedValue = newValue?.value;
    } else if (finalField.type === 'multiselect') {
      changedValue = newValue.map(v => v.value);
    } else {
      changedValue = event.target.value;
      // Boolean değerleri geri çevir
      if (changedValue === 'true') {
        changedValue = true;
      } else if (changedValue === 'false') {
        changedValue = false;
      }
    }

    if (onChange) {
      onChange(finalField.name, changedValue, event);
    }
    
    if (finalField.onChange) {
      finalField.onChange(finalField.name, changedValue, event, formData, onChange);
    }
  };

  const handleBlur = event => {
    if (onBlur) {
      onBlur(finalField.name, value, event);
    }
  };

  const baseProps = {
    disabled: finalField.disabled || disabled,
    fullWidth: true,
    sx: formFieldStyles.field,
  };

  const selectOptions = finalField.type === 'select'
    ? (typeof finalField.options === 'function' ? finalField.options(formData) : finalField.options)
    : [];

  // Auto-select when there's only one option
  useEffect(() => {
    if (finalField.type === 'select' && (value === null || value === undefined || value === '') && Array.isArray(selectOptions) && selectOptions.length === 1) {
      if (onChange) {
        onChange(finalField.name, String(selectOptions[0].value));
      }
    }
  }, [finalField.type, finalField.name, value, selectOptions?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (finalField.type === 'select') {
    const options = selectOptions;

    // Eğer disableSearch flag'i yoksa veya false ise, Autocomplete kullan (arama özellikli)
    if (finalField.disableSearch !== true) {
      return (
        <FormControl {...baseProps} error={!!error}>
          <Autocomplete
            options={options || []}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;
              return String(option.value) === String(value.value || value);
            }}
            // MUI default key=getOptionLabel; aynı label'lı (örn. iki "Baris Akin")
            // option duplicate React key uyarısı verir. value (id) ile unique tut.
            getOptionKey={(option) => (option && option.value != null ? option.value : option)}
            getOptionLabel={option => {
              if (!option) return '';
              if (option.labelKey) return t(option.labelKey);
              if (option.label && option.label.includes('.')) return t(option.label);
              return option.label || '';
            }}
            value={options?.find(opt => String(opt.value) === String(value)) || null}
            onChange={(event, newValue) => {
              const changedValue = newValue?.value;
              // Boolean değerleri geri çevir
              let finalValue = changedValue;
              if (finalValue === 'true') {
                finalValue = true;
              } else if (finalValue === 'false') {
                finalValue = false;
              }
              if (onChange) {
                onChange(finalField.name, finalValue, event);
              }
              if (finalField.onChange) {
                finalField.onChange(finalField.name, finalValue, event, formData, onChange);
              }
            }}
            onBlur={handleBlur}
            disabled={finalField.disabled || disabled}
            renderInput={params => (
              <TextField
                {...params}
                label={finalField.label ? t(finalField.label) : ''}
                placeholder={finalField.placeholder ? t(finalField.placeholder) : ''}
                error={!!error}
                fullWidth
              />
            )}
          />
          {error && <FormHelperText error>{error}</FormHelperText>}
          {!error && finalField.helperText && (
            <FormHelperText 
              error={finalField.helperTextColor === 'error'}
              sx={{ color: finalField.helperTextColor === 'error' ? 'error.main' : 'text.secondary' }}
            >
              {finalField.helperText.includes('.') ? t(finalField.helperText) : finalField.helperText}
            </FormHelperText>
          )}
        </FormControl>
      );
    }
    
    // Normal Select (arama özelliği olmadan)
    return (
      <FormControl {...baseProps}>
        <InputLabel>{finalField.label ? t(finalField.label) : ''}</InputLabel>
        <Select
          value={value === null || value === undefined ? '' : String(value)}
          label={finalField.label ? t(finalField.label) : ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={finalField.placeholder || ''}
        >
          {options?.map(option => (
            <MenuItem key={option.value} value={String(option.value)}>
              {option.labelKey ? t(option.labelKey) : (option.label && option.label.includes('.') ? t(option.label) : (option.label || ''))}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText error>{error}</FormHelperText>}
        {!error && finalField.helperText && (
          <FormHelperText 
            error={finalField.helperTextColor === 'error'}
            sx={{ color: finalField.helperTextColor === 'error' ? 'error.main' : 'text.secondary' }}
          >
            {finalField.helperText.includes('.') ? t(finalField.helperText) : finalField.helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  if (finalField.type === 'autocomplete') {
    const options = typeof finalField.options === 'function' ? finalField.options(formData) : finalField.options;
    
    return (
      <Autocomplete
        {...baseProps}
        options={options || []}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionKey={(option) => (option && option.value != null ? option.value : option)}
        getOptionLabel={option =>
          option.labelKey ? t(option.labelKey) : option.label || option
        }
        value={options?.find(opt => opt.value === value) || null}
        onChange={(event, newValue) => handleChange(event, newValue)}
        onBlur={handleBlur}
        renderInput={params => (
          <TextField
            {...params}
            label={finalField.label ? t(finalField.label) : ''}
            placeholder={finalField.placeholder ? t(finalField.placeholder) : ''}
            error={!!error}
          />
        )}
      />
    );
  }

  if (finalField.type === 'multiselect') {
    const options = typeof finalField.options === 'function' ? finalField.options(formData) : finalField.options;

    return (
      <Autocomplete
        {...baseProps}
        multiple
        options={options || []}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionKey={(option) => (option && option.value != null ? option.value : option)}
        getOptionLabel={option =>
          option.labelKey ? t(option.labelKey) : option.label || option
        }
        value={
          options?.filter(opt => Array.isArray(value) && value.includes(opt.value)) || []
        }
        onChange={(event, newValue) => handleChange(event, newValue)}
        onBlur={handleBlur}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            // Kritik: MUI'nin getTagProps'unda `onClick: undefined` gelebilir.
            // Spread'den SONRA `onClick={undefined}` ile chip'in tiklanabilir
            // moduna gecmesi engellenir (aksi halde Chip tiklamada undefined
            // onClick fonksiyonunu invoke edip "onClick is not a function"
            // firlatir). Chip zaten sadece silme ile calisir, tiklama yok.
            const { key, onDelete: mUIonDelete, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                variant="outlined"
                label={option.labelKey ? t(option.labelKey) : option.label}
                {...tagProps}
                onClick={undefined}
                onDelete={() => {
                  const newValue = Array.isArray(value) ? value.filter(v => v !== option.value) : [];
                  onChange(finalField.name, newValue);
                  if (typeof mUIonDelete === 'function') mUIonDelete();
                }}
              />
            );
          })
        }
        renderInput={params => (
          <TextField
            {...params}
            label={finalField.label ? t(finalField.label) : ''}
            placeholder={finalField.placeholder ? t(finalField.placeholder) : ''}
            error={!!error}
          />
        )}
      />
    );
  }

  return null;
};

export default SelectField;
